import {downloadObjectPictures, extractFileName} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {BaseQueryApi} from "@reduxjs/toolkit/query";

export type UseDownloadObjectPicturesProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadObjectPicturesResult = {
  downloadObjectPictures: (objectId: string, onSuccess: (() => void) | undefined) => void
  downloading: boolean
}

export const useDownloadObjectPictures = (props: UseDownloadObjectPicturesProps): UseDownloadObjectPicturesResult => {
  const {baseQueryApi} = props

  const {t} = useTranslation(["common"])

  // state
  const [_downloading, _setDownloading] = useDebouncedState(false, 500)

  useEffect(() => {
    _setDownloading(_downloading)
  }, [_downloading, _setDownloading])


  useEffect(() => {
    if (_downloading) {
      notifications.show({
        id: "useDownloadObjectPictures_updating",
        title: t("common.status.downloading", "Downloading"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadObjectPictures_updating")
    }
  }, [_downloading, t]);

  const _handleError = useCallback((rejected: any) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _downloadObjectPictures = useCallback((arg: string, onSuccess: (() => void) | undefined) => {
    _setDownloading(true)

    downloadObjectPictures(arg, baseQueryApi)
      .then((response) => {
        const fileName = extractFileName(response)
        response.headers.forEach((h) => console.debug(h))
        response.blob()
          .then((blob) => {
            _setDownloading(false)

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? `${new Date().getTime()}.zip`
            a.click();

            notifications.show({
              color: "green",
              title: t("common.status.downloaded", "Downloaded"),
              message: ""
            });

            if (onSuccess) {
              onSuccess()
            }
          })
          .catch((rejected) => {
            _handleError(rejected)
          })
          .finally(() => {
            _setDownloading(false)
          })
      })
      .catch((rejected) =>
        _handleError(rejected)
      )
      .finally(() => {
        _setDownloading(false)
      })
  }, [_setDownloading, baseQueryApi, t, _handleError])

  return {
    downloadObjectPictures: _downloadObjectPictures,
    downloading: _downloading,
  }
}
